import React, { useEffect, useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import './TagCloud.css';

interface TagCloudItem {
  id: number;
  name: string;
  count: number;
}

interface TagCloudProps {
  selectedTag: string | null;
  onSelectTag: (tag: string | null) => void;
}

const TagCloud: React.FC<TagCloudProps> = ({ selectedTag, onSelectTag }) => {
  const [tags, setTags] = useState<TagCloudItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCloud = async () => {
      try {
        const res = await axiosInstance.get('/api/tags/cloud');
        setTags(res.data);
      } catch (err) {
        console.error('태그 클라우드 로딩 실패:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCloud();
  }, [selectedTag]);

  if (loading) return <div className="tag-cloud-loading">태그 로딩 중...</div>;
  if (tags.length === 0) return null;

  const counts = tags.map(t => t.count);
  const maxCount = Math.max(...counts, 1);
  const minCount = Math.min(...counts, 0);

  const getFontSize = (count: number) => {
    if (maxCount === minCount) return '13px';
    const size = 12 + ((count - minCount) / (maxCount - minCount)) * 10;
    return `${size}px`;
  };

  const getTagColor = (count: number) => {
    if (maxCount === minCount) return 'shadow(0 0 1px)';
    const lightness = 50 - ((count - minCount) / (maxCount - minCount)) * 15;
    return `hsl(210, 80%, ${lightness}%)`;
  };

  return (
    <div className="tag-cloud-container">
      <div className="tag-cloud-header">
        <span className="tag-cloud-title">🏷️ 태그 클라우드</span>
        {selectedTag && (
          <button 
            type="button" 
            onClick={() => onSelectTag(null)} 
            className="tag-filter-reset-btn"
          >
            필터 해제 ✕
          </button>
        )}
      </div>
      <div className="tag-cloud-words">
        {tags.map(tag => {
          const isSelected = selectedTag === tag.name;
          return (
            <span
              key={tag.id}
              onClick={() => onSelectTag(isSelected ? null : tag.name)}
              className={`tag-cloud-item ${isSelected ? 'selected' : ''}`}
              style={{
                fontSize: getFontSize(tag.count),
                color: isSelected ? '#fff' : getTagColor(tag.count),
                cursor: 'pointer',
              }}
              title={`사용 빈도: ${tag.count}회`}
            >
              #{tag.name}
              <span className="tag-count-badge">{tag.count}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default TagCloud;
